/**
 * Status Helper Utilities
 * 
 * Provides standardized status objects for tracking operation states
 * across the application. Status messages are user-friendly and non-technical.
 * 
 * Supports internationalization via optional translation function parameter.
 */

// Import translations
import statusTranslations from './statusHelpers/translations.json';

/**
 * Creates a status object for tracking operation states
 * @param {string} status - One of: 'loading', 'success', 'error', 'idle'
 * @param {string} message - User-friendly message describing the current state
 * @param {string} operation - Name of the operation (e.g., 'culture_audit_reports', 'debrief_schedules')
 * @returns {Object} Status object
 */
export const createStatus = (status, message, operation) => {
  return {
    status, // 'loading' | 'success' | 'error' | 'idle'
    message, // User-friendly message
    operation, // Operation identifier
    timestamp: Date.now(),
  };
};

/**
 * Predefined status messages for common operations
 * These are user-friendly and non-technical
 * @param {Function} t - Optional translation function (e.g., from usePageTranslations)
 * @returns {Object} Status messages object
 */
export const getStatusMessages = (t = null) => {
  const translate = (key, fallback) => {
    if (!t) return fallback;
    const translation = statusTranslations[key];
    if (!translation) return fallback;
    // Get current language from localStorage or default to 'en'
    const language = typeof window !== 'undefined'
      ? (localStorage.getItem('app_language') || 'en')
      : 'en';
    return translation[language] || translation.en || fallback;
  };

  return {
  // Culture Audit Reports
    CULTURE_AUDIT_LOADING: translate('cultureAuditLoading', 'Loading data...'),
    CULTURE_AUDIT_SUCCESS: translate('cultureAuditSuccess', 'Data loaded'),
    CULTURE_AUDIT_ERROR: translate('cultureAuditError', 'Failed to load data'),
    CULTURE_AUDIT_PROCESSING: translate('cultureAuditProcessing', 'Loading data...'),
    CULTURE_AUDIT_COMPLETED: translate('cultureAuditCompleted', 'Data ready'),
    CULTURE_AUDIT_CACHE_LOADING: translate('cultureAuditCacheLoading', 'Preparing data for analysis...'),

    // Debrief Schedules
    DEBRIEF_SCHEDULES_LOADING: translate('debriefSchedulesLoading', 'Loading data...'),
    DEBRIEF_SCHEDULES_SUCCESS: translate('debriefSchedulesSuccess', 'Data loaded'),
    DEBRIEF_SCHEDULES_ERROR: translate('debriefSchedulesError', 'Failed to load data'),

    // General
    LOADING: translate('loading', 'Loading...'),
    SUCCESS: translate('success', 'Operation completed successfully'),
    ERROR: translate('error', 'An error occurred'),
    IDLE: translate('idle', 'Ready'),
  };
};

/**
 * Predefined status messages for common operations (backward compatibility)
 * These are user-friendly and non-technical
 * @deprecated Use getStatusMessages(t) instead for i18n support
 */
export const STATUS_MESSAGES = getStatusMessages();

/**
 * Helper to create loading status
 * @param {string} operation - Name of the operation
 * @param {string} customMessage - Optional custom message
 * @param {Function} t - Optional translation function (e.g., from usePageTranslations)
 */
export const createLoadingStatus = (operation, customMessage = null, t = null) => {
  const messages = getStatusMessages(t);
  const message = customMessage || messages[`${operation.toUpperCase()}_LOADING`] || messages.LOADING;
  return createStatus('loading', message, operation);
};

/**
 * Helper to create success status
 * @param {string} operation - Name of the operation
 * @param {string} customMessage - Optional custom message
 * @param {Function} t - Optional translation function (e.g., from usePageTranslations)
 */
export const createSuccessStatus = (operation, customMessage = null, t = null) => {
  const messages = getStatusMessages(t);
  const message = customMessage || messages[`${operation.toUpperCase()}_SUCCESS`] || messages.SUCCESS;
  return createStatus('success', message, operation);
};

/**
 * Helper to create error status
 * @param {string} operation - Name of the operation
 * @param {string} customMessage - Optional custom message
 * @param {Function} t - Optional translation function (e.g., from usePageTranslations)
 */
export const createErrorStatus = (operation, customMessage = null, t = null) => {
  const messages = getStatusMessages(t);
  const message = customMessage || messages[`${operation.toUpperCase()}_ERROR`] || messages.ERROR;
  return createStatus('error', message, operation);
};

/**
 * Helper to create idle status
 * @param {string} operation - Name of the operation
 * @param {Function} t - Optional translation function (e.g., from usePageTranslations)
 */
export const createIdleStatus = (operation, t = null) => {
  const messages = getStatusMessages(t);
  return createStatus('idle', messages.IDLE, operation);
};

/**
 * Helper to extract user-friendly error message from error object
 * @param {Object|string} error - Error object or error string
 * @param {Function} t - Optional translation function (e.g., from usePageTranslations)
 */
export const extractErrorMessage = (error, t = null) => {
  const translate = (key, fallback) => {
    if (!t) return fallback;
    const translation = statusTranslations[key];
    if (!translation) return fallback;
    const language = typeof window !== 'undefined'
      ? (localStorage.getItem('app_language') || 'en')
      : 'en';
    return translation[language] || translation.en || fallback;
  };

  if (!error) return translate('unexpectedError', 'An unexpected error occurred');
  
  // Try to extract user-friendly message from various error formats
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.detail) return error.detail;
  if (error.data?.detail) return error.data.detail;
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.data?.message) return error.response.data.message;
  
  return translate('errorProcessingRequest', 'An error occurred while processing your request');
};

